import {
  Grid,
  makeStyles,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Box,
  Button,
} from '@material-ui/core';
import {
  HomePageStarredEntities,
  HomePageRecentlyVisited,
  HomePageToolkit,
  WelcomeTitle,
} from '@backstage/plugin-home';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { DevQuote } from '@parsifal-m/plugin-dev-quotes-homepage';
import { QuestionsTableCard } from '@drodil/backstage-plugin-qeta';
import {
  AnnouncementsCard,
  NewAnnouncementBanner,
} from '@backstage-community/plugin-announcements';
import { useAnnouncements } from '@backstage-community/plugin-announcements-react';
import {
  HomePageRequestedReviewsCard,
  HomePageYourOpenPullRequestsCard,
} from '@roadiehq/backstage-plugin-github-pull-requests';
import { Header, Page, Content, Link } from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { CommunitiesOfPracticeCard } from './CommunitiesOfPracticeCard';
import BuildIcon from '@material-ui/icons/Build';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import CloudIcon from '@material-ui/icons/Cloud';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import CodeIcon from '@material-ui/icons/Code';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import FingerprintIcon from '@material-ui/icons/Fingerprint';
import LinkIcon from '@material-ui/icons/Link';
import CompareIcon from '@material-ui/icons/CompareArrows';
import PostAddIcon from '@material-ui/icons/PostAdd';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';

const useStyles = makeStyles(theme => ({
  searchBarInput: {
    maxWidth: '60vw',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '50px',
    boxShadow: theme.shadows[1],
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
  featuredCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6],
    },
  },
  cardAction: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(3),
    flexGrow: 1,
  },
  cardIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  cardDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  chip: {
    marginTop: theme.spacing(1.5),
  },
  cardActions: {
    padding: theme.spacing(0, 3, 3, 3),
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  actionLink: {
    textDecoration: 'none',
  },
  proposeBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(-1),
  },
  devQuoteFooter: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    textAlign: 'center',
    opacity: 0.8,
  },
}));

type CardAction = { label: string; to: string };

type FeaturedItem = {
  title: string;
  description: string;
  icon: string;
  to: string;
  label?: string;
  actions?: CardAction[];
};

const FeaturedCard = ({
  title,
  description,
  icon: Icon,
  to,
  label,
  actions,
}: {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  label?: string;
  actions?: CardAction[];
}) => {
  const classes = useStyles();
  return (
    <Card className={classes.featuredCard}>
      <CardActionArea component={Link} to={to} className={classes.cardAction}>
        <CardContent className={classes.cardContent}>
          <Icon className={classes.cardIcon} />
          <Typography variant="h6" className={classes.cardTitle}>
            {title}
          </Typography>
          <Typography className={classes.cardDescription}>
            {description}
          </Typography>
          {label && (
            <Chip
              label={label}
              size="small"
              color="primary"
              variant="outlined"
              className={classes.chip}
            />
          )}
        </CardContent>
      </CardActionArea>
      {actions && actions.length > 0 && (
        <Box className={classes.cardActions}>
          {actions.map(action => (
            <Link key={action.to} to={action.to} className={classes.actionLink}>
              <Button size="small" color="primary" startIcon={<PostAddIcon />}>
                {action.label}
              </Button>
            </Link>
          ))}
        </Box>
      )}
    </Card>
  );
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  build: BuildIcon,
  book: MenuBookIcon,
  cloud: CloudIcon,
  governance: AccountTreeIcon,
};

const defaultFeaturedItems: FeaturedItem[] = [
  {
    title: 'Architecture Steering Committee',
    description:
      "SaltSquare's ASC — charter, RFCs, decisions, and meeting notes",
    icon: 'governance',
    to: '/catalog/default/component/asc',
    label: 'Governance',
  },
  {
    title: 'Engineering Handbook',
    description:
      "Salt Square's engineering handbook — how we work, communicate, and grow",
    icon: 'book',
    to: '/docs/default/component/salt-eng-handbook',
    label: 'Handbook',
    actions: [
      {
        label: 'Add chapter',
        to: '/create/templates/default/new-handbook-chapter',
      },
    ],
  },
  {
    title: 'Project Hunt',
    description:
      "Salt Square's internal program for building projects with agentic AI development",
    icon: 'build',
    to: '/catalog/default/component/project-hunt',
    label: 'Program',
  },
];

const useFeaturedItems = (): FeaturedItem[] => {
  const config = useApi(configApiRef);
  const configured = config.getOptionalConfigArray('home.featured');
  if (!configured) return defaultFeaturedItems;
  return configured.map(item => ({
    title: item.getString('title'),
    description: item.getString('description'),
    icon: item.getOptionalString('icon') ?? 'build',
    to: item.getString('to'),
    label: item.getOptionalString('label'),
  }));
};

const AnnouncementsSection = () => {
  const { announcements, loading, error } = useAnnouncements({ max: 5 });
  if (loading || error || announcements.count === 0) return null;
  return (
    <>
      <Grid item xs={12}>
        <NewAnnouncementBanner max={1} />
      </Grid>
      <Grid item xs={12}>
        <AnnouncementsCard max={5} />
      </Grid>
    </>
  );
};

const FeaturedCards = () => {
  const items = useFeaturedItems();
  return (
    <>
      {items.map(item => (
        <Grid item xs={12} sm={6} md={3} key={item.to}>
          <FeaturedCard
            title={item.title}
            description={item.description}
            icon={iconMap[item.icon] ?? BuildIcon}
            to={item.to}
            label={item.label}
            actions={item.actions}
          />
        </Grid>
      ))}
      <Grid item xs={12} sm={6} md={3}>
        <CommunitiesOfPracticeCard />
      </Grid>
    </>
  );
};

export const HomePage = () => {
  const classes = useStyles();

  return (
    <Page themeId="home">
      <Header
        title={<WelcomeTitle language={['English']} />}
        pageTitleOverride="Home"
        subtitle="Your central hub for services, documentation, and developer tools"
      />
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SearchContextProvider>
              <HomePageSearchBar
                InputProps={{
                  classes: {
                    root: classes.searchBarInput,
                    notchedOutline: classes.searchBarOutline,
                  },
                }}
                placeholder="Search components, docs, APIs..."
              />
            </SearchContextProvider>
          </Grid>

          <Grid item xs={12} className={classes.proposeBar}>
            <Link
              to="/docs/default/component/salt-eng-handbook/how-to-contribute"
              className={classes.actionLink}
            >
              <Button size="small" color="primary" startIcon={<MenuBookIcon />}>
                How to contribute
              </Button>
            </Link>
            <Link
              to="/create/templates/default/propose-platform-improvement"
              className={classes.actionLink}
            >
              <Button
                size="small"
                color="primary"
                startIcon={<EmojiObjectsIcon />}
              >
                Propose a Backstage / platform improvement
              </Button>
            </Link>
            <Link
              to="/create/templates/default/add-to-catalog"
              className={classes.actionLink}
            >
              <Button
                size="small"
                color="primary"
                startIcon={<LibraryAddIcon />}
              >
                Add to Catalog
              </Button>
            </Link>
          </Grid>

          <AnnouncementsSection />

          <FeaturedCards />

          <Grid item xs={12} md={4}>
            <HomePageRequestedReviewsCard />
          </Grid>
          <Grid item xs={12} md={4}>
            <HomePageYourOpenPullRequestsCard />
          </Grid>
          <Grid item xs={12} md={4}>
            <HomePageToolkit
              title="Toolbox"
              tools={[
                {
                  label: 'Base64',
                  url: '/toolbox/tool/base64-encode',
                  icon: <CodeIcon />,
                },
                {
                  label: 'JWT',
                  url: '/toolbox/tool/jwt-decoder-encode',
                  icon: <VpnKeyIcon />,
                },
                {
                  label: 'JSON',
                  url: '/toolbox/tool/json-converter',
                  icon: <SwapHorizIcon />,
                },
                {
                  label: 'Hash',
                  url: '/toolbox/tool/hash-generate',
                  icon: <FingerprintIcon />,
                },
                {
                  label: 'Strings',
                  url: '/toolbox/tool/string-utilities-convert',
                  icon: <TextFieldsIcon />,
                },
                {
                  label: 'URL',
                  url: '/toolbox/tool/url-encode',
                  icon: <LinkIcon />,
                },
                {
                  label: 'Diff',
                  url: '/toolbox/tool/diff',
                  icon: <CompareIcon />,
                },
              ]}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <HomePageStarredEntities />
          </Grid>
          <Grid item xs={12} md={6}>
            <QuestionsTableCard title="Recent Questions" />
          </Grid>

          <Grid item xs={12}>
            <HomePageRecentlyVisited />
          </Grid>

          <Grid item xs={12} className={classes.devQuoteFooter}>
            <DevQuote />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
